import dtfIndexStakingVault from '@/abis/dtf-index-staking-vault'
import TransactionButton from '@/components/ui/transaction-button'
import { walletAtom } from '@/state/atoms'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { parseUnits } from 'viem'
import { useReadContract, useWaitForTransactionReceipt, useWriteContract } from 'wagmi'
import {
  stakingInputAtom,
  stakingSidebarOpenAtom,
  stTokenAtom,
  unlockBalanceRawAtom,
  unlockDelayAtom,
  updateStTokenSupplyAtom,
} from '../atoms'
import useRefreshVoteLockQueries, {
  VOTE_LOCK_SUBGRAPH_REFRESH_DELAY,
} from '../use-refresh-vote-lock-queries'

const SubmitUnlockButton = () => {
  const account = useAtomValue(walletAtom)
  const stToken = useAtomValue(stTokenAtom)!
  const input = useAtomValue(stakingInputAtom)
  const balance = useAtomValue(unlockBalanceRawAtom)
  const amountToUnlock = parseUnits(input, stToken?.token.decimals)
  const unlockDelay = useAtomValue(unlockDelayAtom)
  const resetInput = useResetAtom(stakingInputAtom)
  const setStakingSidebarOpen = useSetAtom(stakingSidebarOpenAtom)
  const updateStTokenSupply = useSetAtom(updateStTokenSupplyAtom)
  const queryClient = useQueryClient()
  const chainId = stToken?.chainId
  const [isProcessing, setIsProcessing] = useState(false)
  const pendingSupplyDelta = useRef(0n)
  const processedReceiptHash = useRef<string>()
  const processingTimer = useRef<ReturnType<typeof setTimeout>>()
  const {
    invalidateCurrentVoteLockRpcQueries,
    scheduleCurrentVoteLockSubgraphRefresh,
  } = useRefreshVoteLockQueries({ account, stToken })

  useEffect(() => {
    return () => {
      if (processingTimer.current) clearTimeout(processingTimer.current)
    }
  }, [])

  const readyToSubmit =
    !!account && !!balance && amountToUnlock > 0n && amountToUnlock <= balance

  const { data: sharesToBurn } = useReadContract({
    abi: dtfIndexStakingVault,
    functionName: 'previewWithdraw',
    address: stToken?.id,
    args: [amountToUnlock],
    chainId,
    query: { enabled: readyToSubmit && !!stToken?.id },
  })

  const {
    writeContract,
    data: hash,
    isPending: isLoading,
    error,
  } = useWriteContract()

  const write = () => {
    if (!account || !readyToSubmit || !stToken?.id) return

    pendingSupplyDelta.current = -(sharesToBurn ?? amountToUnlock)

    writeContract({
      abi: dtfIndexStakingVault,
      functionName: 'withdraw',
      address: stToken?.id,
      args: [amountToUnlock, account, account],
      chainId,
    })
  }

  const { data: receipt, error: txError } = useWaitForTransactionReceipt({
    hash,
    chainId,
  })

  useEffect(() => {
    if (receipt?.status !== 'success' || !receipt.transactionHash) return
    if (processedReceiptHash.current === receipt.transactionHash) return

    processedReceiptHash.current = receipt.transactionHash
    updateStTokenSupply(pendingSupplyDelta.current)
    invalidateCurrentVoteLockRpcQueries()
    scheduleCurrentVoteLockSubgraphRefresh()
    setIsProcessing(true)
    processingTimer.current = setTimeout(() => {
      resetInput()
      setStakingSidebarOpen(false)
      toast.success('Unlock initiated successfully', { duration: 8000 })
      queryClient.invalidateQueries({ queryKey: ['portfolio'] })
      setIsProcessing(false)
      processingTimer.current = undefined
    }, VOTE_LOCK_SUBGRAPH_REFRESH_DELAY)
  }, [
    receipt?.status,
    receipt?.transactionHash,
    resetInput,
    setStakingSidebarOpen,
    queryClient,
    updateStTokenSupply,
    invalidateCurrentVoteLockRpcQueries,
    scheduleCurrentVoteLockSubgraphRefresh,
  ])

  return (
    <div>
      <TransactionButton
        chain={chainId}
        disabled={receipt?.status === 'success' || !readyToSubmit}
        loading={
          isProcessing ||
          (!receipt && (isLoading || !!hash || (hash && !receipt)))
        }
        loadingText={
          isProcessing
            ? 'Processing transaction...'
            : !!hash
              ? 'Confirming tx...'
              : 'Pending, sign in wallet'
        }
        onClick={write}
        text={
          receipt?.status === 'success'
            ? 'Transaction confirmed'
            : `Begin ${unlockDelay ? `${unlockDelay}-day` : ''} unlock delay`
        }
        className="w-full"
        error={error || txError}
      />
    </div>
  )
}

export default SubmitUnlockButton
